import { handleRequest } from './handleRequest'
import { GtmIds } from '@ta-interaktiv/types/lib/interaktiv.tenant.ch/static/config/GtmIds'
import { Tenants } from '@ta-interaktiv/types'
import { MissingInfoError } from './missingInfoError'
import { catchError as catchErrorTemplate } from './catchError'

/**
 * This endpoint is under our control and not provided by DPD. In cases that
 * changes to the data provided by this endpoint should be necessary,
 * edit the repository under https://gitlab.com/ta-interaktiv/standalone-configuration
 */
const apiEndpoint =
  'https://interaktiv.tagesanzeiger.ch/static/config/gtm-id.json'

function handleData(domainName: string) {
  return (ids: GtmIds) => {
    const id = ids[domainName as keyof typeof Tenants]

    if (!id) {
      throw new MissingInfoError(`No data available for domain ${domainName}`)
    }

    return id
  }
}

const catchError = (subclass: Function) =>
  catchErrorTemplate('getGtmId', subclass)

/**
 * Cache for fetch promises – this way we can repeatedly call these functions,
 * while only one fetch request goes out, reducing the strain on the APIs.
 *
 * Use `clearCache()` and `injectDataInCache()` to manipulate this cache, so
 * it is possible to change the implementation details later on.
 */
let gtmIdPromise: Promise<string> | undefined

/**
 * For testing purposes only – clears the cache.
 */
export function clearCache() {
  gtmIdPromise = undefined
}

/**
 * For testing purposes only – injects data into the cache
 */
export function injectDataInCache(id: string) {
  gtmIdPromise = Promise.resolve(id)
}

/**
 * Gets the correspondonding GTM ID for a domain. Returns null if for some
 * reason the JSON file can't be loaded.
 * 
 * @remarks 
 * This data is under our control, if the GTM IDs need to change, you need to
 * edit the information in the repository
 * https://gitlab.com/ta-interaktiv/standalone-configuration
 * 
 * @param domainName
 */
export function getGtmId(domainName: string): Promise<string | null> {
  if (gtmIdPromise) {
    return gtmIdPromise
  }

  const promise = fetch(apiEndpoint)
    .then(handleRequest<GtmIds>())
    .then(handleData(domainName))
    .catch(catchError(Error))

  gtmIdPromise = promise
  return promise
}
